<template>
  <div class="flex justify-center max-w-full" :class="`membership-slider-${variant}`">
    <div v-scroll="scrollPosition" class="flex overflow-x-scroll text-center md:items-stretch text-gray-600 disable-scrollbars">
      <div
        v-for="(membershipType, index) in membershipTypesData"
        :key="index"
        class="membership-type-box mx-2 relative flex flex-col"
      >
        <div class="membership-image-container mx-auto">
          <!-- TODO Dynamic alt tag (e.g. Model Men One, Model Men Originals, Model Boys Originals )-->
          <OImage
            img-class="rounded-full mx-auto relative"
            :class="{ 'membership-grey-scale': membershipType.value !== orientationId }"
            :mobile="membershipType.image.mobile"
            :desktop="membershipType.image.desktop"
            :src="membershipType.image.fallback"
            :alt="$t('Accessibilty.AltTags.ModelBoxer')"
          />
        </div>
        <div class="membership-type-box-container pt-40 pb-6 flex-1" :class="containerBgColor">
          <h3 v-if="!isLookbookVariant" class="ota-small-semi text-center mb-2" v-text="membershipType.title" />
          <h2 v-if="!isLookbookVariant" class="ota-small-light text-center flex-1" v-text="membershipType.body" />
          <div class="flex flex-col my-4 justify-center items-center md:flex-row">
            <OButton v-if="!isLookbookVariant" variant="dark" :class="{ 'mb-3 md:mb-0 md:mr-3': !hideLookbookButton }" @click="$emit('choose', membershipType.value)">
              {{ $t('HomePage.Choose') }}
            </OButton>
            <OButton
              v-if="!hideLookbookButton"
              class="min-width-button"
              :variant="getButtonVariant(membershipType.value)"
              @click="$emit('explore', membershipType.value)"
            >
              {{ getGhostButtonText(membershipType.title) }}
            </OButton>
          </div>
          <h2 v-if="!isLookbookVariant" class="ota-small-light text-center text-gray-300" v-text="$t('HomePage.MenPrice', { amount: getLocalPrice(membershipType.value) })" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { addLocalCurrency, getImagePaths } from '@/utils/general'
import { Currencies } from '@/enums/general'
import { MenPrices, BoysPrices, OrientationsByGroupIds, GroupIdsByName, Orientations } from '@/enums/products'
const OImage = () => import('@/components/OImage')
const OButton = () => import('@/components/OButton')

export default {
  name: 'MembershipSlider',
  components: {
    OImage,
    OButton
  },
  directives: {
    scroll: {
      inserted (el, { value: scrollValue }) {
        el.scroll(scrollValue, 0)
      }
    }
  },
  props: {
    country: {
      type: String,
      default: 'nl'
    },
    locale: {
      type: String,
      default: 'nl'
    },
    hideLookbookButton: {
      type: Boolean,
      default: false
    },
    orientationId: {
      type: String,
      default: GroupIdsByName[Orientations.menoriginals],
      validator: value => Object.keys(OrientationsByGroupIds).includes(value)
    },
    variant: {
      type: String,
      default: 'default',
      validator: value => ['default', 'lookbook'].some(variant => variant === value)
    },
    showSocks: {
      type: Boolean,
      default: false
    },
    customImages: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    membershipTypesData () {
      const order = this.isLookbookVariant ? [1, 0, 2] : [0, 1, 2]

      const orientationSlugs = {
        0: Orientations.menone,
        1: Orientations.menoriginals,
        2: Orientations.boysoriginals
      }

      const membershipTypes = [...order].map(index => ({
        value: this.$t(`HomePage.MembershipTypes[${index}].value`),
        title: this.$t(`HomePage.MembershipTypes[${index}].title`),
        image: this.customImages && this.customImages[index] ? this.customImages[index] : getImagePaths({
          path: `/images/frontend/static/home-${orientationSlugs[index]}`,
          s3: true
        }),
        body: this.$t(`HomePage.MembershipTypes[${index}].body`)
      }))

      if (this.showSocks) { // TODO: remove this and add socks to MembershipTypes translations like above, when socks are shows across the whole website
        return [...membershipTypes, {
          value: GroupIdsByName[Orientations.socksoriginals],
          title: this.$t('Socks.Socks'),
          image: getImagePaths({
            path: `/images/frontend/static/home-${Orientations.socksoriginals}`,
            s3: true
          }),
          body: this.$t('HomePage.MembershipTypes[1].body')
        }]
      }

      return membershipTypes
    },
    scrollPosition () {
      if (this.isLookbookVariant) {
        if (this.orientationId === GroupIdsByName[Orientations.menone]) {
          return 70
        }

        if (this.orientationId === GroupIdsByName[Orientations.boysoriginals]) {
          return 170
        }

        if (this.orientationId === GroupIdsByName[Orientations.socksoriginals]) {
          return 270
        }

        return 0
      }

      return 150
    },
    isLookbookVariant () {
      return this.variant === 'lookbook'
    },
    containerBgColor () {
      return this.isLookbookVariant ? 'bg-white' : 'bg-gray-50 px-6'
    }
  },
  methods: {
    getLocalPrice (orientationId) {
      return addLocalCurrency(orientationId !== '3' ? MenPrices[this.country] : BoysPrices[this.country], `${this.locale}-${this.country.toUpperCase()}`, Currencies[this.country])
    },
    getGhostButtonText (title) {
      return this.isLookbookVariant ? title : this.$t('HomePage.Explore')
    },
    getButtonVariant (id) {
      return this.isLookbookVariant && id === this.orientationId ? 'primary' : 'ghost'
    }
  }
}
</script>

<style lang="postcss" scoped>
>>> .min-width-button > div {
  min-width: 6rem;
}

.overflow-x-scroll {
  scroll-snap-type: x mandatory;
}

.obutton {
  height: unset;
  min-height: 4rem;
}

.oimage >>> img {
  max-width: 100%;
}

.disable-scrollbars {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none;  /* IE 10+ */

  &::-webkit-scrollbar {
    display: none; /* Chrome/Safari/Webkit */
  }
}

.membership-type-box {
  scroll-snap-align: center;

  &:last-child {
    @apply mr-4;
  }

  &:first-child {
    @apply ml-2;
  }
}

.membership-type-box-container {
  margin-top: -138px;
}

.membership-slider-default {
  .membership-type-box-container {
    @apply flex flex-col;
    min-width: 23rem;
  }
}

.membership-slider-lookbook {
  .membership-image-container {
    max-width: 11.8rem;
  }

  .membership-grey-scale {
    >>> img {
      filter: gray; /* IE6-9 */
      -webkit-filter: saturate(0); /* Google Chrome, Safari 6+ & Opera 15+ */
      filter: saturate(0); /* Microsoft Edge and Firefox 35+ */
    }
  }

  button >>> div {
    @apply whitespace-no-wrap text-md;
  }

  .membership-type-box-container {
    padding-top: 14rem;
  }

  .membership-type-box {
    min-width: unset;
  }
}

@screen md {
  >>> .min-width-button > div {
    min-width: unset;
  }

  .membership-type-box {
    min-width: 35.6rem;
  }

  .membership-slider-lookbook {
    .membership-type-box {
      min-width: unset;
    }
  }
}
</style>
